/**
 * System › Message Library — the shared full-page Detail View's configuration
 * (standing rule 12).
 *
 * The config lives HERE, in the feature folder, rather than as an entry in the
 * shared `moduleCrud.ts`: this screen drives `<SettingsDetailPage>` directly with
 * its own data adapter, so it needs no MODULE_CRUD registration, and keeping it
 * local means adding this form touches no file another form reads.
 *
 * Layout follows the 2026-09-23 standard for NEW detail forms — `sideLabels`
 * (label beside its field, left-aligned, fixed-width column) with `columnMajor`
 * two columns.
 *
 * Field ORDER keeps ALPACA's MessageEditor_DetailView sequence in the left column
 * (MSID · Message · MessageKey · Module · BO · ActionType) so the screen reads
 * against its legacy twin; the columns ALPACA does not have are grouped on the
 * right.
 */
import type { DetailConfig } from '../../modules/SettingsDetailPage';
import { MESSAGES_BASE } from './api';
import type { Vocabularies } from './types';

/** The audit FormName — also the key Settings → Audit Trail Configuration uses to
 *  gate this form's audit log and reason prompt (standing rule 11). */
export const MESSAGE_FORM_NAME = 'Message Library';

/**
 * What may be done with this record's Message Key. Three genuinely different
 * cases, which is why it is not a boolean:
 *
 *  - `new`       — a new message needs a key, so the field is required and open.
 *  - `duplicate` — an existing row whose key collides with another's. Editing one
 *                  of them is the ONLY way to resolve the three legacy
 *                  case-collisions, and until they are resolved the unique index
 *                  cannot be installed.
 *  - `locked`    — every other existing row. Screens look a message up by its key,
 *                  so renaming one in place would silently break every call site;
 *                  the rule is "add a new key, retire the old one".
 *
 * The API enforces the same three cases; this only decides what the form offers.
 */
export type KeyMode = 'new' | 'duplicate' | 'locked';

/**
 * Builds the detail configuration.
 *
 * @param vocab   severity / action-type lists, served by the API so the dropdown
 *                and the server-side validator read ONE list.
 * @param keyMode what may be done with the Message Key — see {@link KeyMode}.
 */
export function messageLibraryConfig(
  vocab: Vocabularies, keyMode: KeyMode,
): DetailConfig {
  return {
    idField: 'oid',
    formName: MESSAGE_FORM_NAME,
    // Drives the detail heading — "Message Library – 002".
    entityLabel: 'Message Library',
    // ROW-major two columns, so the pairs sit side by side exactly as the spec
    // lays them out (MSID | Message Key, Module | BO, Action Type | Severity).
    sideLabels: true,
    // The label column has to hold "Message Key" and "Action Type" without
    // wrapping; 150px is the standard width these forms settled on.
    horizontalLabelWidth: 150,
    fields: [
      {
        key: 'msid',
        label: 'MSID',
        type: 'readonly',
        // ALPACA shows NullText="Autogenerate" on this field; the number is
        // minted server-side on save (race-safe, §14a Rule B).
        placeholder: 'Autogenerated',
      },
      {
        key: 'messageKey',
        label: 'Message Key',
        type: keyMode === 'locked' ? 'readonly' : 'text',
        required: true,
        // The shared default is "{label} Should not be empty." — overridden HERE
        // only, so no other form's wording moves.
        requiredMessage: 'Message Key is required.',
        maxLength: 100,
      },
      {
        key: 'moduleId',
        label: 'Module',
        type: 'select',
        optionsUrl: `${MESSAGES_BASE}/modules`,
      },
      {
        key: 'boId',
        label: 'BO',
        type: 'select',
        optionsUrl: `${MESSAGES_BASE}/bos`,
        // The legacy lstBO cascade: the BO list is the chosen Module's subtree,
        // and changing the Module clears the BO. The shared mechanism fetches
        // `optionsUrl?moduleId={value}`, which is exactly this endpoint's shape.
        dependsOn: 'moduleId',
        enabledWhen: ['moduleId'],
        placeholder: 'Select a Module first',
      },
      {
        key: 'actionType',
        label: 'Action Type',
        // A radio GROUP (owner request): three fixed values that never grow, so
        // showing all of them beats hiding two behind a dropdown.
        type: 'radio',
        options: vocab.actionTypes.map((a) => ({ id: a, name: a })),
        hideBlankOption: true,
        defaultValue: vocab.actionTypes[0] ?? '',
      },
      {
        key: 'severity',
        label: 'Severity',
        type: 'select',
        options: vocab.severities.map((s) => ({ id: s, name: s })),
      },
      { key: 'source', label: 'Source', type: 'readonly' },
      { key: 'status', label: 'Status', type: 'readonly' },
      // When the wording last moved — the question asked of a message whose text
      // someone disputes.
      { key: 'lastUpdated', label: 'Last Updated', type: 'readonly' },
      // The two free-text fields go LAST, each HALF width (owner request
      // 2026-09-28): a message is one or two sentences, so a box spanning both
      // columns was mostly empty air and made the field look unlike every other
      // one on the form.
      //
      // The grid fills left-to-right, so each is followed by a blank spacer — and
      // preceded by one, because the nine fields above them end on a LEFT cell and
      // Message would otherwise land in the right column. That keeps the pair
      // STACKED under the rest of the form: pairing them side by side would put
      // Remark, which is usually empty, level with the one field that always
      // matters.
      { key: 'spacerLastUpdated', label: '', type: 'spacer' },
      {
        key: 'message',
        label: 'Message',
        type: 'textarea',
        required: true,
        requiredMessage: 'Message is required.',
        halfWidth: true,
      },
      { key: 'spacerMessage', label: '', type: 'spacer' },
      { key: 'remark', label: 'Remark', type: 'textarea', halfWidth: true },
      { key: 'spacerRemark', label: '', type: 'spacer' },
      // NOT on the form: NavId and Params. Both are written by the catalog sync
      // from the module's .messages.json and are meaningless on a legacy or
      // admin row, so hand-editing them would only let the two disagree.
    ],
  };
}
